// Fetches a judgment lens's scoped `Finding[]` (the existing single URL-scoped `GET /findings`,
// narrowed by `category`/`source_type`) and derives the shared kanta/salkku overview vocabulary
// from it in the same hook. `LensTopicSection` already needs the raw `Finding[]` for its
// building-scope topic panels (findings list, activity feed, topic metrics), so this composes
// `useScopedFindings` once and exposes both the raw result and the derived `OverviewLensState` —
// never a second `GET /findings` for the portfolio overview
// (docs/spec/concepts.md §2.4 → "Portfolio-scope overview grammar" → judgment-lens fill).
//
// `scopedBuildings` is taken as a parameter rather than resolved internally via a second
// `useBuildings()` + `useFilteredBuildings()` pair: `LensTopicSection` already holds that exact
// list (`scopeBuildings`) for its own findings-list/activity-feed rendering, and `useBuildings()`
// has no shared cache between hook instances (`lib/api/useBuildings.ts` — one `GET /buildings`
// per call site), so a second internal call here would add a real extra request this task's own
// packet forbids ("this task adds no request").
import { useMemo } from "react";

import type { Building } from "@/lib/api/building";
import {
  useScopedFindings,
  type FindingsPerspective,
  type ScopedFindingsResult,
} from "@/lib/api/useScopedFindings";
import { deriveLensFindingsOverview } from "@/lib/lens-findings-overview";
import type { OverviewLensState } from "@/lib/overview-lens";

export interface LensFindingsOverviewResult extends ScopedFindingsResult {
  /** The shared overview vocabulary (situation-card buckets, per-building open questions, city
   *  rollup) derived from the same scoped `Finding[]` this hook already fetched. */
  overview: OverviewLensState;
}

export function useLensFindingsOverview(
  perspective: FindingsPerspective,
  scopedBuildings: Building[],
): LensFindingsOverviewResult {
  const scopedFindings = useScopedFindings(perspective);

  const overview = useMemo(
    () => deriveLensFindingsOverview(scopedBuildings, scopedFindings.findings),
    [scopedBuildings, scopedFindings.findings],
  );

  return { ...scopedFindings, overview };
}
